'use client';

import { useEffect } from 'react';
import { clientLogger } from '@/lib/client-logger';
import {
  reconcilePushPermission,
  refreshPushToken,
} from '@/lib/push-subscription';

const REFRESH_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
const LAST_REFRESH_KEY = 'w4m:push:last-refresh';

function shouldRefresh(): boolean {
  try {
    const last = window.localStorage.getItem(LAST_REFRESH_KEY);
    if (!last) return true;
    const lastMs = parseInt(last, 10);
    if (Number.isNaN(lastMs)) return true;
    return Date.now() - lastMs > REFRESH_INTERVAL_MS;
  } catch {
    // localStorage can be unavailable in private mode — fail safe by refreshing.
    return true;
  }
}

function markRefreshed(): void {
  try {
    window.localStorage.setItem(LAST_REFRESH_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

async function tryRefresh(): Promise<void> {
  if (!shouldRefresh()) return;
  try {
    const ok = await refreshPushToken();
    if (ok) markRefreshed();
  } catch (error: unknown) {
    // Never let a refresh failure surface as an unhandled rejection —
    // e.g. FirebaseError from Installations when NEXT_PUBLIC_FIREBASE_*
    // is missing. The next load/visibility tick retries naturally.
    clientLogger.warn('[push] token refresh failed', { err: error });
  }
}

async function reconcileThenRefresh(): Promise<void> {
  // If the user revoked notification permission while we still hold a
  // registered FCM token, clean it up BEFORE any re-sync attempt.
  try {
    await reconcilePushPermission();
  } catch (error: unknown) {
    clientLogger.warn('[push] permission reconcile failed', { err: error });
  }
  await tryRefresh();
}

export function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        clientLogger.info('[sw] registered', { scope: registration.scope });
        // Re-sync any existing FCM token so a rotated token is captured
        // even before the user takes any action.
        void reconcileThenRefresh();
      })
      .catch((error: unknown) => {
        clientLogger.error('[sw] registration failed', { err: error });
      });

    // Mid-session: re-check on every tab-foreground (debounced via the
    // 1h floor). Catches tokens invalidated while the tab was idle.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void tryRefresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return null;
}
